import type { ReactNode } from 'react';
import { EmptyState } from './basics';

export interface Column<T> {
  key: string;
  label: ReactNode;
  sortable?: boolean;
  width?: string | number;
  num?: boolean;
  render: (row: T) => ReactNode;
}

export function DataTable<T extends { id: number | string }>({
  columns,
  rows,
  sort,
  onSort,
  onRowClick,
  empty = 'Keine Einträge',
  rowClass,
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  sort?: { key: string; order: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  rowClass?: (row: T) => string;
  footer?: ReactNode;
}) {
  return (
    <div className="tw">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${c.num ? 'num' : ''} ${c.sortable && onSort ? 'sortable' : ''}`}
                style={c.width ? { width: c.width } : undefined}
                onClick={c.sortable && onSort ? () => onSort(c.key) : undefined}
                aria-sort={sort?.key === c.key ? (sort.order === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {c.label}
                {sort?.key === c.key && <span className="arrow">{sort.order === 'asc' ? '▲' : '▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState>{empty}</EmptyState>
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.id}
                className={`${onRowClick ? 'row' : ''} ${rowClass?.(r) ?? ''}`}
                onClick={onRowClick ? (e) => {
                  if ((e.target as HTMLElement).closest('a, button, input, select')) return;
                  onRowClick(r);
                } : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.num ? 'num' : ''}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {footer}
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="pager">
      <span>
        {(page - 1) * pageSize + 1} bis {Math.min(page * pageSize, total)} von {total}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)} type="button">
          Zurück
        </button>
        <button className="btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)} type="button">
          Weiter
        </button>
      </span>
    </div>
  );
}
